import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'smart-react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/Modules/Core/Context/AuthContext';
import { AppPageSection } from '@/Modules/Core/Components/Layout/AppPage';
import { EmptyState } from '@/Modules/Core/Components/Layout/EmptyState';
import { PaginationControls } from '@/Modules/Core/Components/Layout/PaginationControls';
import ProjectsCatalogFilters from '../Components/ProjectCatalog/ProjectsCatalogFilters';
import ProjectsCatalogHeader from '../Components/ProjectCatalog/ProjectsCatalogHeader';
import ProjectsGrid from '../Components/ProjectCatalog/ProjectsGrid';
import ProjectsGridSkeleton from '../Components/ProjectCatalog/ProjectsGridSkeleton';
import ProjectsTable from '../Components/ProjectCatalog/ProjectsTable';
import ProjectsTableSkeleton from '../Components/ProjectCatalog/ProjectsTableSkeleton';
import {
  buildRolloutListLocation,
  getCreateRolloutState,
} from '../Components/ProjectCatalog/projectCatalogUtils';
import useProjectsCatalog from '../hooks/useProjectsCatalog';

const NEW_ROLLOUT_PATH = '/projects/create-rollout';

const Projects = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [viewMode, setViewMode] = React.useState('table');
  const {
    currentPage,
    errorMessage,
    filteredProjects,
    loading,
    paginatedProjects,
    refreshProjects,
    reportedTotal,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
    showingFrom,
    showingTo,
    totalPages,
  } = useProjectsCatalog(searchTerm);
  const skeletonCount = Math.min(Number(rowsPerPage || 8), 8);
  const canCreateRollout = hasPermission('rollouts.create');
  const canViewRollouts = hasPermission('rollouts.view');

  const handleCreatePageRollout = React.useCallback(() => {
    if (!canCreateRollout) {
      return;
    }

    navigate(NEW_ROLLOUT_PATH);
  }, [canCreateRollout, navigate]);

  const handleCreateRollout = React.useCallback(
    (project) => {
      if (!canCreateRollout) {
        return;
      }

      if (!project.projectId || !project.versionId) {
        return;
      }

      navigate(NEW_ROLLOUT_PATH, {
        state: getCreateRolloutState(project),
      });
    },
    [canCreateRollout, navigate]
  );

  const handleCreateAccumulative = React.useCallback(
    (project) => {
      if (!canCreateRollout) {
        return;
      }

      if (!project.projectId || !project.versionId) {
        return;
      }

      navigate(`/createrollout/${project.projectId}/${project.versionId}/accum/0`);
    },
    [canCreateRollout, navigate]
  );

  const handleOpenRollouts = React.useCallback(
    (project) => {
      if (!canViewRollouts) {
        return;
      }

      const rolloutListLocation = buildRolloutListLocation(project);

      if (!rolloutListLocation) {
        return;
      }

      navigate(rolloutListLocation.pathname, {
        state: rolloutListLocation.state,
      });
    },
    [canViewRollouts, navigate]
  );

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        setSearchTerm('');
      }}
    >
      <div className="tw">
        <div className="space-y-6">
          <AppPageSection>
            <div className="space-y-6">
              <ProjectsCatalogHeader
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onRefresh={refreshProjects}
                onCreateRollout={handleCreatePageRollout}
                canCreateRollout={canCreateRollout}
              />

              <ProjectsCatalogFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                showingFrom={showingFrom}
                showingTo={showingTo}
                filteredCount={filteredProjects.length}
                reportedTotal={reportedTotal}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={setRowsPerPage}
              />

              {loading ? (
                viewMode === 'table' ? (
                  <ProjectsTableSkeleton count={Math.min(skeletonCount, 6)} />
                ) : (
                  <ProjectsGridSkeleton count={skeletonCount} />
                )
              ) : errorMessage ? (
                <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-[13px] text-destructive">
                  {errorMessage}
                </div>
              ) : paginatedProjects.length === 0 ? (
                <EmptyState
                  icon={Search}
                  title={
                    searchTerm
                      ? 'No projects matched your search'
                      : 'No projects available'
                  }
                  description={
                    searchTerm
                      ? 'Try a different project name or clear the search field.'
                      : 'Projects will appear here once they are available to browse.'
                  }
                />
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    <ProjectsGrid
                      projects={paginatedProjects}
                      onOpenRollouts={handleOpenRollouts}
                      onCreateRollout={handleCreateRollout}
                      onCreateAccumulative={handleCreateAccumulative}
                      canCreateRollout={canCreateRollout}
                      canOpenRollouts={canViewRollouts}
                    />
                  ) : (
                    <ProjectsTable
                      projects={paginatedProjects}
                      onOpenRollouts={handleOpenRollouts}
                      onCreateRollout={handleCreateRollout}
                      onCreateAccumulative={handleCreateAccumulative}
                      canCreateRollout={canCreateRollout}
                      canOpenRollouts={canViewRollouts}
                    />
                  )}

                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </>
              )}
            </div>
          </AppPageSection>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Projects;
